/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unnecessary-type-assertion */
import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { TooltipModule } from "primeng/tooltip";
import { TagModule } from "primeng/tag";
import { DividerModule } from "primeng/divider";
import { ButtonModule } from "primeng/button";
import { DialogService } from "primeng/dynamicdialog";
import { ScheduledOrder } from "../../../../core/models/scheduled-order.interface";
import { Site } from "../../../../core/models/site.interface";
import { ValidationCheckItem } from "../../../../core/models/validation-check-item.interface";
import { ShippingRateCalculationErrorsDialogComponent } from "../shipping-rate-calculation-errors-dialog/shipping-rate-calculation-errors-dialog.component";

/**
 * Validation Checks Inline View Component
 * Displays validation check results for a scheduled order
 * Modern checklist design based on best UI practices
 */
@Component({
  selector: "app-validation-checks-inline-view",
  standalone: true,
  imports: [
    CommonModule,
    TooltipModule,
    TagModule,
    DividerModule,
    ButtonModule,
  ],
  providers: [DialogService],
  templateUrl: "./validation-checks-inline-view.component.html",
  styleUrls: ["./validation-checks-inline-view.component.scss"],
})
export class ValidationChecksInlineViewComponent implements OnInit, OnChanges {
  @Input() scheduledOrder!: ScheduledOrder;
  @Input() site?: Site;
  @Input() inlineMode: boolean = true;
  @Input() showLastProcessingCycle: boolean = false;

  // Events for last processing cycle actions
  @Output() viewCycleDetails = new EventEmitter<number>();
  @Output() viewProcessingLogs = new EventEmitter<any>();

  validationCheckItems: ValidationCheckItem[] = [];

  constructor(private dialogService: DialogService) {}

  /**
   * Get total count of checks
   */
  get totalChecks(): number {
    return this.validationCheckItems.filter((item) => !item.isCheckSkipped)
      .length;
  }

  /**
   * Get count of passed checks
   */
  get passedChecks(): number {
    return this.validationCheckItems.filter(
      (item) =>
        !item.isCheckFailed && !item.isCheckSkipped && !item.isCheckWarning,
    ).length;
  }

  /**
   * Get count of failed checks
   */
  get failedChecks(): number {
    return this.validationCheckItems.filter((item) => item.isCheckFailed)
      .length;
  }

  /**
   * Get count of warning checks
   */
  get warningChecks(): number {
    return this.validationCheckItems.filter((item) => item.isCheckWarning)
      .length;
  }

  /**
   * Check if all validations passed
   */
  get allChecksPassed(): boolean {
    return this.failedChecks === 0 && this.warningChecks === 0;
  }

  ngOnInit(): void {
    this.initValidationCheckItems();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["scheduledOrder"]) {
      this.initValidationCheckItems();
    }
  }

  /**
   * Initialize validation check items based on the scheduled order's ValidationResult
   */
  initValidationCheckItems(): void {
    if (!this.scheduledOrder) {
      return;
    }

    this.validationCheckItems = [];

    // Get shipping rate error additional response if available
    let shippingRateErrorAdditionalResponse: string | undefined;
    if (
      this.scheduledOrder.ValidationResult &&
      this.scheduledOrder.ValidationResult.ErrorItems
    ) {
      const errors = this.scheduledOrder.ValidationResult.ErrorItems;
      const shippingRateError = errors.find(
        (e) => e.ProcessingErrorCode === 1002,
      );

      if (shippingRateError && shippingRateError.ScheduledOrderFailureReason) {
        shippingRateErrorAdditionalResponse =
          shippingRateError.ScheduledOrderFailureReason;
      }
    }

    // Return early if validation result exists but failed without error items
    if (
      this.scheduledOrder.ValidationResult &&
      !this.scheduledOrder.ValidationResult.Succeeded &&
      !this.scheduledOrder.ValidationResult.ErrorItems
    ) {
      return;
    }

    // Initialize all validation check items
    this.validationCheckItems.push(
      this.createValidationCheckItem(
        1004,
        "Payment Method set",
        "No Payment Method exists",
      ),
    );

    this.validationCheckItems.push(
      this.createValidationCheckItem(
        1003,
        "Payment Integration set",
        "No Payment Integration exists",
      ),
    );

    this.validationCheckItems.push(
      this.createValidationCheckItem(
        1000,
        "Contains items",
        "Scheduled Order is Empty",
      ),
    );

    this.validationCheckItems.push(
      this.createValidationCheckItem(
        1001,
        "Items are valid",
        "Items are invalid",
      ),
    );

    this.validationCheckItems.push(
      this.createValidationCheckItem(
        1002,
        "Shipping Rate set",
        "No Shipping Rate found",
        shippingRateErrorAdditionalResponse,
      ),
    );

    // Check if each validation check failed
    for (const item of this.validationCheckItems) {
      item.isCheckFailed = this.isCheckFailed(item.processingErrorCode);
    }

    // Set skip logic based on dependencies
    // If "Contains items" failed, skip "Items are valid" and "Shipping Rate set"
    if (this.validationCheckItems[2].isCheckFailed) {
      this.validationCheckItems[3].isCheckSkipped = true;
      this.validationCheckItems[4].isCheckSkipped = true;
    }

    // If "Items are valid" failed, skip "Shipping Rate set"
    if (this.validationCheckItems[3].isCheckFailed) {
      this.validationCheckItems[4].isCheckSkipped = true;
    }

    // If "Payment Method set" failed, skip "Payment Integration set"
    if (this.validationCheckItems[0].isCheckFailed) {
      this.validationCheckItems[1].isCheckSkipped = true;
    }

    // Check for shipping rate calculation warnings
    if (this.hasShippingRateCalculationErrors(this.validationCheckItems[4])) {
      this.validationCheckItems[4].isCheckWarning = true;
      this.validationCheckItems[4].isCheckShippingRateWarning = true;
    }
  }

  /**
   * Create a validation check item
   */
  private createValidationCheckItem(
    processingErrorCode: number,
    successText: string,
    errorText: string,
    additionalErrorText?: string,
  ): ValidationCheckItem {
    return {
      processingErrorCode,
      errorText,
      successText,
      additionalErrorText,
      isCheckFailed: null,
      isCheckSkipped: false,
      isCheckWarning: false,
      isCheckShippingRateWarning: false,
    };
  }

  /**
   * Check if a validation check failed based on processing error code
   */
  private isCheckFailed(processingErrorCode: number): boolean | null {
    if (this.scheduledOrder.ValidationResult) {
      const errors = this.scheduledOrder.ValidationResult.ErrorItems;
      if (errors) {
        return (
          errors.find((e) => e.ProcessingErrorCode === processingErrorCode) !==
          undefined
        );
      }
    }

    return null;
  }

  /**
   * Check if there are shipping rate calculation errors
   */
  private hasShippingRateCalculationErrors(
    validationCheckItem: ValidationCheckItem,
  ): boolean {
    return (
      validationCheckItem.processingErrorCode === 1002 &&
      !!this.scheduledOrder.Metadata &&
      !!(this.scheduledOrder.Metadata as any).ShippingRateCalculationErrors &&
      (this.scheduledOrder.Metadata as any).ShippingRateCalculationErrors
        .length > 0
    );
  }

  /**
   * Open shipping rates calculation errors popup
   */
  openShippingRatesCalculationErrorsPopup(): void {
    this.dialogService.open(ShippingRateCalculationErrorsDialogComponent, {
      header: `Scheduled Order ${this.scheduledOrder.Id}`,
      width: "50vw",
      modal: true,
      closable: true,
      closeOnEscape: true,
      breakpoints: {
        "1199px": "75vw",
        "575px": "90vw",
      },
      style: {
        "max-height": "80vh",
      },
      data: {
        scheduledOrder: this.scheduledOrder,
        site: this.site,
      },
    });
  }

  /**
   * Handle click on "view last cycle details" link
   */
  onViewCycleDetails(): void {
    if (this.scheduledOrder?.LastProcessingCycle?.Id) {
      this.viewCycleDetails.emit(this.scheduledOrder.LastProcessingCycle.Id);
    }
  }

  /**
   * Handle click on "view processing logs" link
   */
  onViewProcessingLogs(): void {
    if (this.scheduledOrder?.LastProcessingCycle) {
      this.viewProcessingLogs.emit(this.scheduledOrder.LastProcessingCycle);
    }
  }
}
